import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { PricingItem, PricingItemFilters, PricingItemTableRow } from '../pricing-numbers';
import { ConfirmDialogComponent } from '../../../shared/dialog/confirm-dialog/confirm-dialog.component';
import CONST from '../get-number.type';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import { AmplitudeService } from '../../../shared/services/amplitude.service';
import { ConfigService } from '../../../config/config.service';
import { MatDialog, MatSort, MatTableDataSource } from '@angular/material';
import { getDocumentsSelector, getPricingSelector } from '../pricing-numbers.reducer';
import { AccountSettingsService } from '../../../my-account/account-settings.service';
import { InfoDialogComponent } from '../../../shared/dialog/info-dialog/info-dialog.component';
import { RegionForNumberChosenAction, SetAvailableNumberTypesAction } from '../pricing-numbers.action';
import { Observable } from 'rxjs/Observable';
import { Router } from '@angular/router';

@Component({
  selector: 'cc-pricing-numbers-table',
  templateUrl: './pricing-numbers-table.component.html',
  styleUrls: ['./pricing-numbers-table.component.scss'],
})
export class PricingNumbersTableComponent implements OnInit, OnChanges {
  @ViewChild(MatSort) sort: MatSort;
  @Input() filters: PricingItemFilters;
  @Input() isFullyConfigured: boolean;
  public displayedColumns = ['prefix', 'city', 'type', 'priceSetup', 'price', 'requiredDocumentsCodes', 'action'];
  public dataSource: MatTableDataSource<PricingItemTableRow>;
  public allPossibleDocuments$: Observable<object>;
  private isTrial: boolean;

  constructor(
    private store: Store<IState>,
    private accountSettingsService: AccountSettingsService,
    private amplitudeService: AmplitudeService,
    private configService: ConfigService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    this.amplitudeService.logEvent('Panel:Numbers:Order: Init');

    this.loadPricing();
    this.setFilters();
    this.accountSettingsService.getSubscription().subscribe((subscription) => {
      this.isTrial = subscription.status === 'TRIAL';
    });
  }

  ngOnChanges() {
    this.setFilters();
  }

  public getNumber(pricingItem: PricingItem) {
    if (this.isTrial && this.isFullyConfigured) {
      this.amplitudeService.logEvent('Panel:Numbers:Order Cannot order - trial');
      this.showCannotOrderNumberDueToTrialDialog().subscribe((confirm) => {
        if (confirm) {
          this.amplitudeService.logEvent('Panel:Numbers:Order Cannot order - upgrade plan');
          this.router.navigateByUrl('/my-account');
        }
      });
      return;
    }

    if (!this.isFullyConfigured && !pricingItem.availableForInitialNumber) {
      this.amplitudeService.logEvent(
        'Panel:Numbers:Order Cannot order initial number - number not available instantly',
        this.prepareNumberAmplitudeObject(pricingItem)
      );
      this.showNotAvailableForInitialNumberDialog().subscribe();
      return;
    }

    this.amplitudeService.logEvent('Panel:Numbers:Order: City and prefix chosen', {
      ...this.prepareNumberAmplitudeObject(pricingItem),
    });

    this.store.dispatch(
      new RegionForNumberChosenAction({
        fullyConfigured: this.isFullyConfigured,
        pricingId: pricingItem.id,
      })
    );
  }

  private loadPricing() {
    this.store.select(getPricingSelector).subscribe((pricing: PricingItem[]) => {
      const availableNumberTypes = new Set<string>();
      const updatedPricing: PricingItemTableRow[] = pricing.map((pricingItem) => {
        availableNumberTypes.add(pricingItem.type);
        return {
          ...pricingItem,
          numberType: this.getNumberType(pricingItem),
          buttonLabel: this.getButtonLabel(pricingItem),
          hasRequiredDocuments: this.hasRequiredDocuments(pricingItem),
        };
      });
      this.dataSource = new MatTableDataSource(updatedPricing);

      setTimeout(() => {
        this.setAvailableNumbersTypes(availableNumberTypes);
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = (data, filter: string) => {
          const filterObject: PricingItemFilters = this.filters;
          const search = filterObject.search.trim().toLowerCase();
          const type = filterObject.type;
          const region = filterObject.region;

          const isCorrectPrefix = data.prefix.toLowerCase().includes(search);
          const isCorrectCity = data.city.toLowerCase().includes(search);
          const isCorrectType = data.type.includes(type) || data.city.toLowerCase() === type.toLowerCase();
          const isCorrectRegion = !region || data.region === region;

          return (isCorrectPrefix || isCorrectCity) && isCorrectType && isCorrectRegion;
        };
      });
    });

    this.allPossibleDocuments$ = this.store.select(getDocumentsSelector);
  }

  private showCannotOrderNumberDueToTrialDialog() {
    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'ORDER_NUMBER.TRIAL_MODAL.TITLE',
          body: 'ORDER_NUMBER.TRIAL_MODAL.TEXT',
          confirmButton: 'ORDER_NUMBER.TRIAL_MODAL.CONFIRM',
        },
      })
      .afterClosed();
  }

  private getButtonLabel(pricingItem: PricingItem) {
    return this.isFree(pricingItem) ? 'ORDER_NUMBER.GET_FREE' : 'ORDER_NUMBER.BUY';
  }

  private showNotAvailableForInitialNumberDialog() {
    return this.dialog
      .open(InfoDialogComponent, {
        data: {
          title: 'ORDER_NUMBER.NOT_AVAILABLE_FOR_INITIAL.TITLE',
          body: 'ORDER_NUMBER.NOT_AVAILABLE_FOR_INITIAL.BODY',
        },
      })
      .afterClosed();
  }

  private hasRequiredDocuments(pricingItem: PricingItem): boolean {
    return !!pricingItem.requiredDocumentsCodes.length;
  }

  private getNumberType(pricingItem: PricingItem) {
    if (!pricingItem.availableToBuyWithoutSupport) {
      return CONST.TYPE.ORDER;
    }
    return CONST.TYPE.BUY;
  }

  private setFilters(filters = this.filters) {
    if (this.dataSource) {
      const newFiltersString = JSON.stringify(filters);
      const currentFiltersString = this.dataSource.filter;

      if (newFiltersString !== currentFiltersString) {
        setTimeout(() => {
          this.dataSource.filter = JSON.stringify(filters);
        });
      }
    }
  }

  private prepareNumberAmplitudeObject(pricingItem: PricingItem) {
    const {
      availableToBuyWithoutSupport,
      city,
      countryCode,
      prefix,
      numberType,
      price,
      priceSetup,
      requiredDocumentsCodes,
    } = pricingItem;

    return {
      availableToBuyWithoutSupport,
      city,
      countryCode,
      prefix,
      numberType,
      priceMonthly: price,
      priceSetup,
      requiredDocumentsCodes,
    };
  }

  isFree(element: PricingItem) {
    return !this.isFullyConfigured && element.availableForInitialNumber;
  }

  private setAvailableNumbersTypes(availableNumberTypes: Set<string>) {
    this.store.dispatch(new SetAvailableNumberTypesAction(Array.from(availableNumberTypes)));
  }
}
